import React from 'react';
import { IndianRupee, Pencil, Plus, ShoppingBag, Users } from 'lucide-react';

import {
  CardAction,
  ChipRow,
  EmptyState,
  PageHeader,
  Pager,
  PhonePage,
  PillButton,
  ScreenLoader,
  SearchBar,
  StatusPill,
} from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * Every customer, on a phone.
 *
 * The desktop list is six columns and a rail that fills when a row is picked.
 * Here each customer is a card that says who they are, how to reach them and
 * — for the desks that may see it — what they owe; tapping the card opens the
 * full profile, which is what the rail was standing in for.
 */

const money = (v) => `₹${(v || 0).toLocaleString('en-IN')}`;

function CustomerCard({ customer: c, showFinancials, onOpen, onEdit, onNewOrder, onRecordPayment }) {
  const owing = (c.outstanding || 0) > 0;
  const contact = [c.mobile_number, c.email].filter(Boolean).join(' · ');

  return (
    <li
      data-testid={`customer-card-${c.id}`}
      className="mt-3 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid={`view-customer-${c.id}`}
        className="block w-full px-4 pt-3 text-left"
      >
        <span className="block truncate text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
          {c.customer_name}
        </span>
        <span className="mt-0.5 block truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>
          {c.firm_name || '—'}
        </span>
        <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
          {contact || 'No contact on file'}
          {c.gstin ? ` · ${c.gstin}` : ''}
        </span>

        <div className="flex items-center justify-between gap-2 py-3 text-[12px]">
          <span style={{ color: Colors.text.secondary }}>
            {c.total_orders || 0} order{c.total_orders === 1 ? '' : 's'}
            {showFinancials ? ` · ${money(c.total_value)} billed` : ''}
          </span>
          {showFinancials && (
            <StatusPill
              testID={`customer-outstanding-${c.id}`}
              label={owing ? `${money(c.outstanding)} due` : 'Paid up'}
              tone={owing ? 'critical' : 'positive'}
              dot
            />
          )}
        </div>
      </button>

      <div className="flex items-center gap-2 overflow-x-auto border-t px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ borderColor: Colors.border.subtle }}>
        <CardAction icon={Pencil} label="Edit" onClick={onEdit} testID={`edit-customer-${c.id}`} />
        <CardAction icon={ShoppingBag} label="New order" tone="accent" onClick={onNewOrder} testID={`order-for-customer-${c.id}`} />
        {onRecordPayment && (
          <CardAction icon={IndianRupee} label="Payment" tone="positive" onClick={onRecordPayment} testID={`pay-customer-${c.id}`} />
        )}
      </div>
    </li>
  );
}

export default function PhoneCustomersList({
  customers, total, loading, error, page, totalPages, onPage, onSearch,
  owingOnly, onOwingOnly, owingCount, showFinancials,
  onRefresh, onNew, onOpen, onEdit, onNewOrder, onRecordPayment,
}) {
  const chips = [
    { key: 'all', label: `All ${total}` },
    showFinancials && { key: 'owing', label: `Owing ${owingCount}` },
  ].filter(Boolean);

  return (
    <PhonePage testID="customers-page">
      <PageHeader
        title="Customers"
        subtitle={`${total} on file`}
        action={(
          <PillButton onClick={onNew} testID="add-customer-btn">
            <Plus className="h-4 w-4" /> New
          </PillButton>
        )}
      />

      <div className="px-5">
        <SearchBar placeholder="Name, firm, phone…" onChange={onSearch} testID="customer-search" />
        {chips.length > 1 && (
          <div className="mt-3">
            <ChipRow chips={chips} active={owingOnly ? 'owing' : 'all'} onSelect={(key) => onOwingOnly(key === 'owing')} testIDPrefix="chip" />
          </div>
        )}
      </div>

      <div className="flex-1 px-5 pb-8">
        {loading ? (
          <ScreenLoader />
        ) : error ? (
          <div className="py-16 text-center">
            <p className="text-[15px] font-medium" style={{ color: Colors.status.error }}>Failed to load customers</p>
            <p className="mt-1 text-[13px]" style={{ color: Colors.text.tertiary }}>{error}</p>
            <button
              type="button"
              onClick={onRefresh}
              className="mt-4 rounded-[10px] border px-5 py-2 text-[13px] font-bold"
              style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
            >
              Try again
            </button>
          </div>
        ) : customers.length === 0 ? (
          <EmptyState icon={Users} message="No customers found" hint="Try another search, or add the first one." />
        ) : (
          <ul data-testid="customers-list">
            {customers.map((c) => (
              <CustomerCard
                key={c.id}
                customer={c}
                showFinancials={showFinancials}
                onOpen={() => onOpen(c)}
                onEdit={() => onEdit(c)}
                onNewOrder={() => onNewOrder(c)}
                onRecordPayment={onRecordPayment ? (() => onRecordPayment(c)) : null}
              />
            ))}
          </ul>
        )}
        <Pager page={page} totalPages={totalPages} onPage={onPage} testID="customers-pagination" />
      </div>
    </PhonePage>
  );
}
